import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Spinner } from '@economic/gaudi';
import { refreshCurrentLegacyPage, analytics } from '@economic/react-app';
import { t } from '../../l10n/l10n';
import { useLocalization } from '../../l10n/l10n';
import useQuery from '../../../hooks/useQuery';
import StepChooseSubscription from './StepChooseSubscription';
import StepFeedback from './StepFeedback';
import StepSuccess from './StepSuccess';
import { CompanyDeregisterData } from './types';
import internalApiClient from '../../../util/api/internalApiClient';
import { RouteDisclosure } from '../../RouteDisclosure';
import './style.css';

enum Steps {
    ChooseSubscription = 1,
    Feedback = 2,
    Success = 3,
}

function FooterError({ error }) {
    if (error === null || error === undefined || error === '') {
        return null;
    }
    return (
        <div className="company-deregister-dialog error-message">
            <div className="company-deregister-dialog error-message-body">{error}</div>
        </div>
    );
}

export default function CompanyDeregisterDialog() {
    const navigate = useNavigate();
    const location = useLocation();
    const loadingTexts = useLocalization(textIds);
    const [show, setShow] = useState(true);
    const [error, setError] = useState('');
    const [step, setStep] = useState(Steps.ChooseSubscription);
    const [state, setState] = useState({
        isArchiveSubscriptionChosen: false,
        reason: '',
        recommendationScale: '',
        comments: '',
        competitorName: '',
        tellUsMore: '',
    });

    const { loading, data } = useQuery<CompanyDeregisterData>('company/deregisterData');

    if (loadingTexts || !show || loading) {
        return null;
    }

    if (step === Steps.ChooseSubscription && data.skipArchiveLicenseChoice) {
        // jump to second step as if archive option is not chosen.
        saveArchiveOptionAndNext(false);
    }

    const disclosure = <RouteDisclosure routeTrigger={'#deregister-company'} />;

    function back() {
        setError('');
        if (step > 1) setStep(step - 1);
    }

    function next() {
        setError('');
        setStep(step + 1);
    }

    function saveArchiveOptionAndNext(archiveSubscriptionChosen: boolean) {
        setState({ ...state, isArchiveSubscriptionChosen: archiveSubscriptionChosen });
        next();
    }

    async function saveFeedbackAndDeregisterCompany(
        reason: string,
        recommendationScaleScore: string,
        comments: string,
        competitorName: string,
        tellUsMore: string
    ) {
        var requestModel = {
            switchToArchiveSubscription: state.isArchiveSubscriptionChosen,
            reasonId: parseInt(reason),
            score: parseInt(recommendationScaleScore),
            comments: comments,
            competitorName: competitorName,
            pleaseTellUsMore: tellUsMore,
        };

        await internalApiClient
            .post('/company/Deregister', requestModel)
            .then(_ => {
                analytics.gtm('license cancelled', {
                    gaCategory: 'account',
                    gaAction: 'license cancelled',
                    gaLabel: reason,
                });
                setState({
                    ...state,
                    reason: reason,
                    recommendationScale: recommendationScaleScore,
                    comments: comments,
                    competitorName: competitorName,
                    tellUsMore: tellUsMore,
                });
                next();
            })
            .catch(error => setError(error.error || error.response.data));
    }

    function close() {
        setShow(false);
        refreshCurrentLegacyPage('content');
        navigate(location.pathname);
    }

    const getSuccessMessage = (isArchiveSubscriptionChosen: boolean) => {
        if (isArchiveSubscriptionChosen) {
            return t(`FORM_COMPANY_DEREGISTER_SUCCESS_CONTENT_1`, {
                ARCHIVE_DATE: data.archiveSubscriptionStartDate,
                DEREG_DATE: data.archiveSubscriptionEndDate,
            });
        } else {
            return t(`FORM_COMPANY_DEREGISTER_SUCCESS_CONTENT_2`, {
                DEREG_DATE: data.deregistrationDate,
            });
        }
    };

    return (
        <Modal disclosure={disclosure} hideOnEsc={true} hideOnClickOutside={false} onHide={close} size="sm">
            {loading && !data ? (
                <Spinner />
            ) : (
                <>
                    {step === Steps.ChooseSubscription && (
                        <StepChooseSubscription
                            deregisterData={data}
                            onDone={saveArchiveOptionAndNext}
                            onError={error => setError(error)}
                        />
                    )}
                    {step === Steps.Feedback && (
                        <StepFeedback
                            deregisterData={data}
                            onBack={data.skipArchiveLicenseChoice ? undefined : back}
                            onDone={saveFeedbackAndDeregisterCompany}
                            onError={error => setError(error)}
                        />
                    )}
                    {step === Steps.Success && (
                        <StepSuccess onDone={close} text={getSuccessMessage(state.isArchiveSubscriptionChosen)} />
                    )}
                    {<FooterError error={error} />}
                </>
            )}
        </Modal>
    );
}

const textIds = [
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_TITLE',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_CONTENT_1',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_CONTENT_2',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_CONTENT_3',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_ERROR_CHOOSE_OPTION',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_ARCHIVE',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_DEREGISTER',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_LEARNMORE',
    'FORM_COMPANY_DEREGISTER_CHOOSESUBSCRIPTION_NEXT',
    'FORM_COMPANY_DEREGISTER_FEEDBACK_TITLE',
    'FORM_COMPANY_DEREGISTER_FEEDBACK_CONTENT',
    'DEREGISTRATION_FORM_SCETION_TITLE_REASONS',
    'DEREGISTRATION_FORM_COMMENTS_TEXT',
    'DEREGISTRATION_FORM_TELL_US_COMPETITOR_NAME',
    'DEREGISTRATION_FORM_WHAT_FUNCTIONALITY_MISSING',
    'DEREGISTRATION_FORM_WHO_ARE_YOU_CHANGING_TO',
    'DEREGISTRATION_FORM_SCETION_REASONS_DEFAULT_ITEM',
    'DEREGISTRATION_FORM_COMPANYCLOSED_OPTIONS_DEFAULT_ITEM',
    'DEREGISTRATION_FORM_VALIDATION_PLEASE_CHOOSE_REASON',
    'DEREGISTRATION_FORM_VALIDATION_PLEASE_CHOOSE_RATING',
    'FORM_COMPANY_DEREGISTER_FEEDBACK_RECOMMENDATION_SCALE_TEXT',
    'DEREGISTRATION_FORM_MAIN_REASONS_CHANGE_ACCOUNTING_PROGRAM',
    'DEREGISTRATION_FORM_MAIN_REASONS_COMPANY_CLOSING',
    'DEREGISTRATION_FORM_MAIN_REASONS_STARTING_ANOTHER_AGR',
    'DEREGISTRATION_FORM_MAIN_REASONS_TEST_AGR_FOR_DEV',
    'FORM_COMPANY_DEREGISTER_FEEDBACK_NEXT',
    'FORM_COMPANY_DEREGISTER_FEEDBACK_BACK',
    'FORM_COMPANY_DEREGISTER_SUCCESS_TITLE',
    'FORM_COMPANY_DEREGISTER_SUCCESS_CONTENT_1',
    'FORM_COMPANY_DEREGISTER_SUCCESS_CONTENT_2',
    'FORM_COMPANY_DEREGISTER_SUCCESS_CLOSE',
    'DEREGISTRATION_FORM_SCETION_RATING_DEFAULT_ITEM',
];
